import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Lock, CheckCircle, Clock, AlertCircle, Shield, Sparkles, Users, FileText, Receipt, BarChart3, Building2 } from 'lucide-react';

const ProjectFeatures = () => {
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password === 'Gayatri9*') {
      setIsAuthenticated(true);
      setError('');
    } else {
      setError('Incorrect password');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <Lock className="w-6 h-6 text-blue-600" />
            </div>
            <CardTitle>Protected Page</CardTitle>
            <p className="text-gray-500 text-sm mt-2">Enter password to view project features</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full"
              />
              {error && <p className="text-red-500 text-sm">{error}</p>}
              <Button type="submit" className="w-full">
                <Shield className="w-4 h-4 mr-2" />
                Access Features
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">GSTAssist - Complete Feature Summary</h1>
          <p className="text-gray-600">Comprehensive GST Practice Management & Client Portal Platform</p>
          <p className="text-sm text-gray-500 mt-2">Tech Stack: React + FastAPI + MongoDB</p>
          <p className="text-xs text-green-600 mt-1">Last Updated: February 15, 2026</p>
        </div>

        {/* NEW: Client Portal & Business Invoicing */}
        <Card className="mb-6 border-2 border-green-500">
          <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-green-500" />
              NEW: Client Portal & Business Invoicing Module
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-orange-500" />
                  Client Portal (/client-portal)
                </h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                    <span><strong>Separate Login</strong> - Clients have own authentication</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                    <span><strong>Compliance Dashboard</strong> - View pending tasks, overdue items</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                    <span><strong>Invoice Viewing</strong> - See invoices from CA firm</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                    <span><strong>Document Upload</strong> - Share GST returns, bank statements</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                    <span><strong>Messaging</strong> - Two-way chat with CA firm</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <Receipt className="w-4 h-4 text-blue-500" />
                  Business Invoicing ("My Business" Tab)
                </h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                    <span><strong>GST Sales Invoices</strong> - Create invoices for client's customers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                    <span><strong>Customer Management</strong> - B2B/B2C with GSTIN, balance tracking</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                    <span><strong>Product Catalog</strong> - HSN codes, GST rates (0-28%)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                    <span><strong>Vendor Management</strong> - Track suppliers, purchases</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                    <span><strong>GST Reports</strong> - Monthly summary for GSTR-1/3B</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                    <span><strong>Firm Visibility</strong> - CA can view client's business data</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Portal Management (/portal-management):</strong> Firm users can create portal accounts for clients, 
                manage access, toggle active/inactive status, and view messages from all clients.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Compliance & Automation */}
        <Card className="mb-6 border-2 border-purple-500">
          <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              Compliance & Automation Engine
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h4 className="font-semibold text-purple-800 mb-2">Compliance Automation</h4>
                <ul className="text-sm text-purple-700 space-y-1">
                  <li>- Rule-based task auto-generation</li>
                  <li>- Recurring task scheduling</li>
                  <li>- Escalation rules for overdue</li>
                </ul>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h4 className="font-semibold text-purple-800 mb-2">Reminder Automation</h4>
                <ul className="text-sm text-purple-700 space-y-1">
                  <li>- Configurable reminder rules</li>
                  <li>- Multi-channel (Email/WhatsApp/SMS)</li>
                  <li>- Custom templates</li>
                </ul>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h4 className="font-semibold text-purple-800 mb-2">Recurring Invoices</h4>
                <ul className="text-sm text-purple-700 space-y-1">
                  <li>- Monthly retainer billing</li>
                  <li>- Auto-generation scheduler</li>
                  <li>- Email distribution</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Intelligence & Analytics */}
        <Card className="mb-6 border-2 border-blue-500">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-500" />
              Intelligence & Analytics Suite
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">Advanced Dashboard</h4>
                <p className="text-sm text-blue-700">Compliance heatmap, revenue charts, workload visualization</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">Staff Productivity</h4>
                <p className="text-sm text-blue-700">Performance metrics, tasks completed, revenue per staff</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">Client Health Score</h4>
                <p className="text-sm text-blue-700">Green/Yellow/Red scoring, risk prioritization</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">Audit Logs</h4>
                <p className="text-sm text-blue-700">Immutable tracking of all user actions</p>
              </div>
            </div>
            <div className="mt-4 p-3 bg-indigo-50 rounded-lg">
              <p className="text-sm text-indigo-800">
                <strong>Platform Analytics (Super Admin):</strong> Aggregate statistics across all users - 
                total users, clients, invoices, revenue trends.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Core Modules */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              Core Modules
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { name: 'Authentication', features: 'Signup, Login, Password Reset, Email Verification, Onboarding Wizard' },
                { name: 'Client Management', features: 'CRUD, GSTIN fetch, Bulk upload, Payment tracking' },
                { name: 'Services', features: 'CRUD, Pricing types, HSN/SAC codes' },
                { name: 'Client-Services', features: 'Assignments, Billing toggle' },
                { name: 'Invoices', features: 'Auto-generate, Ad-hoc, PDF, WhatsApp share, Excel export' },
                { name: 'Receipts', features: 'CRUD, Partial payments, Reports' },
                { name: 'Expenses', features: 'CRUD, Categories, Reports' },
                { name: 'Staff Management', features: 'CRUD, Roles, Permissions' },
                { name: 'Task Management', features: 'CRUD, Templates, Auto-generation, Status tracking' },
                { name: 'Documents (AI)', features: 'Upload, OCR, AI Summarization, Search' },
                { name: 'GST Returns', features: 'Status tracking, Sync with GST Portal' },
                { name: 'WhatsApp', features: 'Web + Business API, Bulk messaging' },
              ].map((module, idx) => (
                <div key={idx} className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <h3 className="font-semibold text-green-800">{module.name}</h3>
                  </div>
                  <p className="text-sm text-green-700">{module.features}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Client Portal API Endpoints */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Client Portal API Endpoints</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold mb-3 text-gray-700">Portal Authentication</h3>
                <ul className="space-y-1 text-sm text-gray-600">
                  <li><code className="bg-gray-100 px-1 rounded">POST /api/client-portal/auth/login</code></li>
                  <li><code className="bg-gray-100 px-1 rounded">POST /api/client-portal/auth/register</code></li>
                  <li><code className="bg-gray-100 px-1 rounded">GET /api/client-portal/me</code></li>
                  <li><code className="bg-gray-100 px-1 rounded">GET /api/client-portal/dashboard</code></li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-3 text-gray-700">Portal Features</h3>
                <ul className="space-y-1 text-sm text-gray-600">
                  <li><code className="bg-gray-100 px-1 rounded">GET /api/client-portal/invoices</code></li>
                  <li><code className="bg-gray-100 px-1 rounded">GET/POST /api/client-portal/documents</code></li>
                  <li><code className="bg-gray-100 px-1 rounded">GET/POST /api/client-portal/messages</code></li>
                  <li><code className="bg-gray-100 px-1 rounded">GET /api/client-portal/users</code> (firm)</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-3 text-gray-700">Business Invoicing</h3>
                <ul className="space-y-1 text-sm text-gray-600">
                  <li><code className="bg-gray-100 px-1 rounded">GET/PUT /api/client-portal/business/settings</code></li>
                  <li><code className="bg-gray-100 px-1 rounded">CRUD /api/client-portal/business/customers</code></li>
                  <li><code className="bg-gray-100 px-1 rounded">CRUD /api/client-portal/business/products</code></li>
                  <li><code className="bg-gray-100 px-1 rounded">CRUD /api/client-portal/business/vendors</code></li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-3 text-gray-700">Business Reports</h3>
                <ul className="space-y-1 text-sm text-gray-600">
                  <li><code className="bg-gray-100 px-1 rounded">GET/POST /api/client-portal/business/sales-invoices</code></li>
                  <li><code className="bg-gray-100 px-1 rounded">GET /api/client-portal/business/reports/gst-summary</code></li>
                  <li><code className="bg-gray-100 px-1 rounded">GET /api/clients/{'{id}'}/business-data</code> (firm)</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Frontend Routes */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Frontend Pages/Routes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <h3 className="font-semibold mb-3 text-gray-700">Client Portal (New)</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex justify-between"><span className="text-green-600">/client-portal</span><span className="text-gray-500">Client Login & Dashboard</span></li>
                  <li className="flex justify-between"><span className="text-green-600">/portal-management</span><span className="text-gray-500">Firm: Manage Portal Users</span></li>
                </ul>
                <h3 className="font-semibold mb-3 mt-4 text-gray-700">Analytics (New)</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex justify-between"><span className="text-blue-600">/analytics-dashboard</span><span className="text-gray-500">Advanced Dashboard</span></li>
                  <li className="flex justify-between"><span className="text-blue-600">/audit-logs</span><span className="text-gray-500">Audit Trail</span></li>
                  <li className="flex justify-between"><span className="text-blue-600">/reminder-automation</span><span className="text-gray-500">Automation Panel</span></li>
                  <li className="flex justify-between"><span className="text-blue-600">/platform-analytics</span><span className="text-gray-500">Super Admin Stats</span></li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-3 text-gray-700">User Pages</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex justify-between"><span>/dashboard</span><span className="text-gray-500">Dashboard</span></li>
                  <li className="flex justify-between"><span>/clients</span><span className="text-gray-500">Clients</span></li>
                  <li className="flex justify-between"><span>/services</span><span className="text-gray-500">Services</span></li>
                  <li className="flex justify-between"><span>/invoices</span><span className="text-gray-500">Invoices</span></li>
                  <li className="flex justify-between"><span>/receipts</span><span className="text-gray-500">Receipts</span></li>
                  <li className="flex justify-between"><span>/expenses</span><span className="text-gray-500">Expenses</span></li>
                  <li className="flex justify-between"><span>/tasks</span><span className="text-gray-500">Tasks</span></li>
                  <li className="flex justify-between"><span>/staff</span><span className="text-gray-500">Staff Management</span></li>
                  <li className="flex justify-between"><span>/documents</span><span className="text-gray-500">Documents (AI)</span></li>
                  <li className="flex justify-between"><span>/whatsapp</span><span className="text-gray-500">WhatsApp</span></li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-3 text-gray-700">Public Pages</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex justify-between"><span>/</span><span className="text-gray-500">Landing Page</span></li>
                  <li className="flex justify-between"><span>/forgot-password</span><span className="text-gray-500">Forgot Password</span></li>
                  <li className="flex justify-between"><span>/verify-email</span><span className="text-gray-500">Email Verification</span></li>
                </ul>
                <h3 className="font-semibold mb-3 mt-4 text-gray-700">Admin Pages</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex justify-between"><span>/admin</span><span className="text-gray-500">Admin Dashboard</span></li>
                  <li className="flex justify-between"><span>/admin/users</span><span className="text-gray-500">User Management</span></li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Security Features */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-500" />
              Security Features
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                'JWT-based authentication (separate tokens for firm & client portal)',
                'Password hashing with SHA256',
                'Session isolation (localStorage cleared on login)',
                'Role-based access control (user, admin, super_admin, client_portal)',
                'Email verification for new accounts',
                'Secure password reset flow with tokens',
                'Audit logging for all critical actions',
                'Data isolation per user/firm',
              ].map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Database Collections */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Database Collections (MongoDB)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                'users', 'clients', 'services', 'client_services',
                'invoices', 'receipts', 'expenses', 'staff',
                'tasks', 'task_templates', 'documents', 'messages',
                'compliance_rules', 'escalation_rules', 'reminder_rules', 'reminder_logs',
                'audit_logs', 'client_portal_users', 'client_portal_messages', 'client_portal_documents',
                'client_business_settings', 'client_customers', 'client_vendors', 'client_products',
                'client_sales_invoices', 'client_purchases'
              ].map((collection, idx) => (
                <div key={idx} className="bg-gray-100 rounded px-3 py-2 text-sm font-mono">
                  {collection}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Planned Features */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-yellow-500" />
              Planned/Future Features
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { name: 'Subscription & Billing Engine', priority: 'P0' },
                { name: 'Online Payment (Razorpay)', priority: 'P0' },
                { name: 'e-Invoice / e-Way Bill (GST API)', priority: 'P1' },
                { name: 'PDF Invoice Download', priority: 'P1' },
                { name: 'WhatsApp automation for task updates', priority: 'P2' },
                { name: 'Tally Integration', priority: 'P2' },
                { name: 'Mobile App', priority: 'P3' },
              ].map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2 text-gray-600">
                  <Clock className="w-4 h-4 text-yellow-500" />
                  <span>{feature.name}</span>
                  <span className={`ml-auto text-xs px-2 py-0.5 rounded ${
                    feature.priority === 'P0' ? 'bg-red-100 text-red-700' :
                    feature.priority === 'P1' ? 'bg-orange-100 text-orange-700' :
                    feature.priority === 'P2' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>{feature.priority}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Test Credentials */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-500" />
              Test Credentials
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2">Firm User</h4>
                <p className="text-sm"><strong>Email:</strong> test.user@gstassist.demo</p>
                <p className="text-sm"><strong>Password:</strong> Test@123</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2">Client Portal</h4>
                <p className="text-sm"><strong>Email:</strong> client.portal3@test.com</p>
                <p className="text-sm"><strong>Password:</strong> Portal@123</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2">Super Admin</h4>
                <p className="text-sm"><strong>Email:</strong> arunkumargstp@gmail.com</p>
                <p className="text-sm"><strong>Password:</strong> Test@123</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-gray-500 text-sm">
          <p>Last Updated: February 15, 2026</p>
          <p className="mt-1">GSTAssist - Built with React, FastAPI, MongoDB</p>
          <p className="mt-1 text-xs">Backend: 14,000+ lines | Frontend: 50+ components</p>
        </div>
      </div>
    </div>
  );
};

export default ProjectFeatures;
